import React from 'react';
import { 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle,
  Box,
  Avatar,
  Slide
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import {
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  severity?: 'info' | 'warning' | 'error';
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmText = 'Подтвердить',
  cancelText = 'Отмена',
  onConfirm,
  onCancel,
  severity = 'warning'
}: ConfirmDialogProps) {
  const getButtonColor = () => {
    switch (severity) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'primary';
    }
  };

  const getIcon = () => {
    switch (severity) {
      case 'error':
        return <ErrorIcon sx={{ fontSize: 40 }} />;
      case 'warning':
        return <WarningIcon sx={{ fontSize: 40 }} />;
      default:
        return <InfoIcon sx={{ fontSize: 40 }} />;
    }
  };

  const getGradient = () => {
    switch (severity) {
      case 'error':
        return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
      case 'warning':
        return 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)';
      default:
        return 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
    }
  };

  const getAvatarBg = () => {
    switch (severity) {
      case 'error':
        return 'rgba(245, 87, 108, 0.1)';
      case 'warning':
        return 'rgba(250, 112, 154, 0.1)';
      default:
        return 'rgba(79, 172, 254, 0.1)';
    }
  };

  const getIconColor = () => {
    switch (severity) {
      case 'error':
        return '#f5576c';
      case 'warning':
        return '#fa709a';
      default:
        return '#4facfe';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      TransitionComponent={Transition}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
          maxWidth: 500,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: getGradient(),
          }
        }
      }}
    >
      <DialogTitle 
        id="alert-dialog-title"
        sx={{ 
          pt: 4,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <Avatar
          sx={{
            bgcolor: getAvatarBg(),
            color: getIconColor(),
            width: 56,
            height: 56,
          }}
        >
          {getIcon()}
        </Avatar>
        <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
          {title}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pb: 2 }}>
        <DialogContentText 
          id="alert-dialog-description"
          sx={{ 
            fontSize: '1rem',
            color: 'text.primary',
            ml: 9
          }}
        >
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button 
          onClick={onCancel}
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {cancelText}
        </Button>
        <Button 
          onClick={onConfirm} 
          color={getButtonColor()} 
          variant="contained"
          autoFocus
          sx={{
            borderRadius: 2,
            px: 3,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '&:hover': {
              boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
            }
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}